import { LICENSE_MODULE } from "@selector/shared";
import { SmartLink } from "@selector/shared/components";
import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import { type FC, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Navigate, Route, Routes, useMatch } from "react-router";
import { SymbolIcon } from "widgets";

import { AdminToolsPage } from "@/ui-modules/admin/AdminToolsPage.tsx";
import JobsAdminPage from "@/ui-modules/admin/JobsAdminPage.tsx";
import PlaybooksAdminPage from "@/ui-modules/admin/PlaybooksAdminPage.tsx";

import { useDocumentTitle } from "../../app-utils/hooks/useDocumentTitle.js";
import { type Configuration } from "../../graphql.js";
import { isLicensed } from "../../redux/modules/config.js";
import { type IAppState } from "../../redux/reducer.js";
import { ErrorPage404 } from "../core/errors/InformationPage.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import tabStyle from "../core/tabs/styles/Tabs.module.scss";
import ProjectGroupsAdminPage from "./ProjectGroupsAdminPage.js";
import RolesAdminPage from "./RolesAdminPage.js";
import SamplesAdminPage from "./SamplesAdminPage.js";
import SystemAdminPage from "./SystemAdminPage.js";
import UserAdminPage from "./UserAdminPage.js";

const ADMIN_USERS_KEY = "users";
const ADMIN_ROLES_KEY = "roles";
const ADMIN_CONTENT_PERMISSIONS_KEY = "projectGroups";
const ADMIN_SAMPLES_KEY = "samples";
const ADMIN_TOOLS_KEY = "tools";
const ADMIN_SYSTEM_KEY = "system";
const ADMIN_PLAYBOOKS_KEY = "playbooks";
const ADMIN_JOBS_KEY = "jobs";

interface AdminTabProps {
  key: string;
  title: string;
  icon: MaterialSymbol;
}
/**
 * Admin Page main component
 */
const AdminPage: FC = () => {
  useDocumentTitle("Administration");
  const match = useMatch("admin/:adminKey/*");
  const adminKey = match?.params.adminKey;

  // Configuration already loaded by NavBar
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration, shallowEqual);

  const tabs: AdminTabProps[] = useMemo(() => {
    const tabs: AdminTabProps[] = [];
    tabs.push(
      {
        key: ADMIN_USERS_KEY,
        title: "Users",
        icon: "group",
      },
      {
        key: ADMIN_ROLES_KEY,
        title: "Roles",
        icon: "badge",
      },
      {
        key: ADMIN_CONTENT_PERMISSIONS_KEY,
        title: "User Groups",
        icon: "groups",
      },
    );

    if (isLicensed(configuration, LICENSE_MODULE.TRACKER)) {
      tabs.push({
        key: ADMIN_SAMPLES_KEY,
        title: "Samples",
        icon: "lab_panel",
      });
    }

    tabs.push({
      key: ADMIN_TOOLS_KEY,
      title: "Admin Tools",
      icon: "handyman",
    });

    if (isLicensed(configuration, LICENSE_MODULE.ANALYST) || isLicensed(configuration, LICENSE_MODULE.PROCESSOR)) {
      tabs.push({
        key: ADMIN_SYSTEM_KEY,
        title: "System",
        icon: "dns",
      });
    }

    tabs.push(
      {
        key: ADMIN_JOBS_KEY,
        title: "Jobs",
        icon: "work",
      },
      {
        key: ADMIN_PLAYBOOKS_KEY,
        title: "Playbooks",
        icon: "desktop_windows",
      },
    );

    return tabs;
  }, [configuration]);

  return (
    <>
      <div className={styleLayout.main_tabs}>
        <div className={tabStyle.tab_list} style={{ marginBottom: 1 }}>
          {tabs.map((tab) => {
            return (
              <SmartLink
                className={clsx(tabStyle.tab, { [tabStyle.tab_selected]: adminKey === tab.key })}
                key={tab.key}
                to={`/admin/${tab.key}`}
              >
                <SymbolIcon icon={tab.icon} />
                <span>{tab.title}</span>
              </SmartLink>
            );
          })}
        </div>
      </div>

      <Routes>
        <Route index element={<Navigate to={`/admin/${ADMIN_USERS_KEY}`} replace />} />
        <Route path={`${ADMIN_USERS_KEY}`} element={<UserAdminPage />}></Route>
        <Route path={`${ADMIN_ROLES_KEY}/*`} element={<RolesAdminPage />}></Route>
        <Route path={`${ADMIN_CONTENT_PERMISSIONS_KEY}/*`} element={<ProjectGroupsAdminPage />}></Route>
        <Route path={`${ADMIN_SAMPLES_KEY}/*`} element={<SamplesAdminPage />}></Route>
        <Route path={`${ADMIN_TOOLS_KEY}`} element={<AdminToolsPage />}></Route>
        <Route path={`${ADMIN_SYSTEM_KEY}/*`} element={<SystemAdminPage />}></Route>
        <Route path={`${ADMIN_PLAYBOOKS_KEY}/*`} element={<PlaybooksAdminPage />}></Route>
        <Route path={`${ADMIN_JOBS_KEY}/*`} element={<JobsAdminPage />}></Route>
        <Route path="/*" element={<ErrorPage404 />}></Route>
      </Routes>
    </>
  );
};

export { AdminPage };
